import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import Slider from 'react-slick';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Tooltip } from 'react-tooltip';
import { DerivAffiliateBanner } from './DerivAffiliate';
import './ProductPage.css';
import { Helmet, HelmetProvider } from 'react-helmet-async';

function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [setFiles, setSetFiles] = useState([]);
  const [error, setError] = useState('');
  const [{ isPending }, paypalDispatch] = usePayPalScriptReducer();
  const sliderRef = useRef(null);
  const sliderContainerRef = useRef(null);
  const purchaseAsideRef = useRef(null);
  const [showDialog, setShowDialog] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [proceedToPayPal, setProceedToPayPal] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [isSetFilesOpen, setIsSetFilesOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewBody, setReviewBody] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [reviewError, setReviewError] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [isPurchasePinned, setIsPurchasePinned] = useState(false);
  const [purchaseCardStyle, setPurchaseCardStyle] = useState({});

  const isFreeProduct = product?.price === 0 || product?.price === '0';
  const isLoggedIn = !!localStorage.getItem('userToken');

  useEffect(() => {
    if (!id) {
      setError('No product ID provided');
      return;
    }

    const fetchData = async () => {
      try {
        const productResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/products/${id}`);
        const screenshotsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/products/${id}/screenshots`);
        const setFilesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/products/${id}/set-files`);
        const reviewsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/products/${id}/reviews`);
        setProduct(productResponse.data);
        setScreenshots(screenshotsResponse.data);
        setSetFiles(setFilesResponse.data);
        setReviews(Array.isArray(reviewsResponse.data) ? reviewsResponse.data : []);
        setPromoCode('');
        setPromoError('');
        setPromoSuccess('');
        setDiscountedPrice(null);
        setProceedToPayPal(false);
        setShowDialog(false);
        setAcceptedTerms(false);
        setIsSetFilesOpen(false);
        setReviewStatus('');
        setReviewError('');
      } catch (err) {
        setError('Product not found');
        console.error('Fetch error:', err);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
    if (!clientId) {
      return;
    }
      paypalDispatch({
        type: 'resetOptions',
        value: {
          'client-id': clientId,
          components: 'buttons',
          currency: 'USD',
          intent: 'capture',
          vault: true,
        },
      });
  }, [paypalDispatch]);

  useEffect(() => {
    if (!screenshots.length || !sliderContainerRef.current || !sliderRef.current) return;

    const container = sliderContainerRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (sliderRef.current) {
          if (entry.isIntersecting) {
            sliderRef.current.slickPlay();
          } else {
            sliderRef.current.slickPause();
          }
        }
      },
      { threshold: 0.5 }
    );

    if (container) {
      observer.observe(container);
    }

    return () => {
      if (container && observer) {
        observer.unobserve(container);
      }
    };
  }, [screenshots]);

  useEffect(() => {
    const updatePurchaseCardPosition = () => {
      if (!purchaseAsideRef.current || window.innerWidth <= 1024) {
        setIsPurchasePinned(false);
        setPurchaseCardStyle({});
        return;
      }

      const navOffset = 108;
      const asideRect = purchaseAsideRef.current.getBoundingClientRect();
      const shouldPin = asideRect.top <= navOffset;

      if (shouldPin) {
        setPurchaseCardStyle({
          left: `${asideRect.left}px`,
          width: `${asideRect.width}px`,
        });
      } else {
        setPurchaseCardStyle({});
      }

      setIsPurchasePinned(shouldPin);
    };

    updatePurchaseCardPosition();
    window.addEventListener('scroll', updatePurchaseCardPosition, { passive: true });
    window.addEventListener('resize', updatePurchaseCardPosition);

    return () => {
      window.removeEventListener('scroll', updatePurchaseCardPosition);
      window.removeEventListener('resize', updatePurchaseCardPosition);
    };
  }, [product]);

  const handleApplyPromo = async () => {
    if (!promoCode) {
      setPromoError('Please enter a promo code');
      setPromoSuccess('');
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/validate-promo`, { code: promoCode });
      if (response.data.success) {
        const discountPercentage = response.data.discount_percentage;
        const newPrice = product.price * (1 - discountPercentage / 100);
        setDiscountedPrice(Math.max(0.01, parseFloat(newPrice.toFixed(2))));
        setPromoSuccess(`Promo code applied! ${discountPercentage}% off`);
        setPromoError('');
      }
    } catch (err) {
      setPromoError(err.response?.data?.error || 'Failed to validate promo code');
      setPromoSuccess('');
      setDiscountedPrice(null);
    }
  };

  const createOrder = async () => {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/create-payment`, {
      productId: id,
      promoCode: promoSuccess ? promoCode : null,
    });
    return response.data.id;
  };

  const onApprove = async (data) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/capture-payment`, {
        orderID: data.orderID,
        promoCode: promoSuccess ? promoCode : null,
      });
      if (response.data.success) {
        navigate('/success', {
          state: {
            productName: product.name,
            filePath: response.data.filePath,
            orderID: data.orderID,
            purchaseId: response.data.purchaseId,
            productType: response.data.productType,
            purchaseDate: new Date().toLocaleString(),
            paymentAmount: discountedPrice || product.price,
          },
        });
      } else {
        navigate('/payment-failed', { state: { productName: product.name, paymentAmount: discountedPrice || product.price } });
      }
    } catch (err) {
      navigate('/payment-failed', { state: { productName: product.name, paymentAmount: discountedPrice || product.price } });
    }
  };

  const handleBuyNowClick = () => {
    setShowDialog(true);
  };

  const handleConfirm = () => {
    if (acceptedTerms) {
      setProceedToPayPal(true);
      setShowDialog(false);
    }
  };

  const handleFreeDownload = () => {
    navigate(`/download/${id}`);
  };

  const handleSetFileDownload = (filePath) => {
    const downloadLink = document.createElement('a');
    downloadLink.href = `${process.env.REACT_APP_API_URL}/${filePath}`;
    downloadLink.download = filePath.split('/').pop();
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const toggleSetFiles = () => {
    setIsSetFilesOpen(!isSetFilesOpen);
  };

  const sliderSettings = {
    dots: true,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  const typeLabelMap = {
    expert_advisor: 'Expert Advisor',
    indicator: 'Indicator',
    source_code: 'Source Code',
  };

  const handleSubmitReview = async (event) => {
    event.preventDefault();
    if (!isLoggedIn) return;
    setReviewSubmitting(true);
    setReviewError('');
    setReviewStatus('');
    try {
      const token = localStorage.getItem('userToken');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/products/${id}/reviews`,
        {
          rating: reviewRating,
          title: reviewTitle,
          body: reviewBody,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setReviewStatus('Review submitted for approval.');
      setReviewTitle('');
      setReviewBody('');
    } catch (err) {
      setReviewError(err.response?.data?.error || 'Failed to submit review.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  return (
    <HelmetProvider>
      <div className="product-page page-shell">
        <Helmet>
          <title>{product ? `${product.name} - Vix Algo Trading Store` : 'Loading Product'}</title>
          <meta name="description" content={product ? `Buy ${product.name} - ${product.type} for trading.` : 'Loading product details...'} />
        </Helmet>
        {error ? (
          <div className="section-card">{error}</div>
        ) : !product ? (
          <div className="loading-container"><div className="loading-spinner"></div></div>
        ) : (
          <>
            <section className="product-hero">
              <div className="product-hero-content">
                <span className="tag">{typeLabelMap[product.type] || 'Trading Tool'}</span>
                <h1>{product.name}</h1>
                {product.currentVersion && (
                  <p className="product-version">Version {product.currentVersion}</p>
                )}
                <p className="product-summary">
                  Built for systematic traders who value disciplined entries, risk control, and clarity.
                </p>
                <div className="product-price">
                  {isFreeProduct ? (
                    <span className="price-main">Free</span>
                  ) : discountedPrice !== null ? (
                    <>
                      <span className="price-original">${product.price}</span>
                      <span className="price-main">${discountedPrice}</span>
                    </>
                  ) : (
                    <span className="price-main">${product.price}</span>
                  )}
                </div>
                <div className="product-meta">
                  <div>
                    <p className="meta-label">License</p>
                    <p className="meta-value">2 devices per license</p>
                  </div>
                  <div>
                    <p className="meta-label">Delivery</p>
                    <p className="meta-value">Instant download</p>
                  </div>
                  {product.currentVersion && (
                    <div>
                      <p className="meta-label">Current version</p>
                      <p className="meta-value">{product.currentVersion}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="product-hero-visual">
                <div className="product-media-card">
                  {product.imagePath && (
                    <img src={`${process.env.REACT_APP_API_URL}/${product.imagePath}`} alt={product.name} />
                  )}
                </div>
              </div>
            </section>

            <section className="product-layout">
              <div className="product-main">
                <div className="section-card">
                  <h2>Overview</h2>
                  <div className="product-description">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{product.description}</ReactMarkdown>
                  </div>
                </div>

                {setFiles.length > 0 && (
                  <div className="section-card">
                    <div className="set-files-header">
                      <h2>Set files</h2>
                      <button className="ghost-button" onClick={toggleSetFiles} type="button">
                        {isSetFilesOpen ? 'Hide set files' : 'View set files'}
                      </button>
                    </div>
                    <div className={`set-files-container ${isSetFilesOpen ? 'open' : ''}`}>
                      {setFiles.map((setFile) => (
                        <div key={setFile.id} className="set-file-item">
                          <span className="set-file-symbol">{setFile.symbol}</span>
                          <span
                            className="info-icon"
                            data-tooltip-id={`tooltip-${setFile.id}`}
                            data-tooltip-content={setFile.note || 'No additional notes'}
                          >
                            i
                          </span>
                          <Tooltip
                            id={`tooltip-${setFile.id}`}
                            place="top"
                            style={{
                              backgroundColor: '#ffffff',
                              color: '#000000',
                              zIndex: 1000,
                              maxWidth: '90vw',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              padding: '8px',
                              borderRadius: '5px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            }}
                          />
                          <button
                            className="download-set-file"
                            onClick={() => handleSetFileDownload(setFile.filePath)}
                            type="button"
                          >
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {screenshots.length > 0 && (
                  <div className="section-card" ref={sliderContainerRef}>
                    <h2>Media</h2>
                    <div className="screenshot-slider">
                      <Slider ref={sliderRef} {...sliderSettings}>
                        {screenshots.map((screenshot) => (
                          <div key={screenshot.id}>
                            {screenshot.type === 'video' ? (
                              <div className="video-container">
                                <iframe
                                  src={screenshot.screenshotPath}
                                  title={`Video ${screenshot.id}`}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                ></iframe>
                              </div>
                            ) : (
                              <div className="image-container">
                                <a href={`${process.env.REACT_APP_API_URL}/${screenshot.screenshotPath}`} target="_blank" rel="noopener noreferrer">
                                  <img
                                    src={`${process.env.REACT_APP_API_URL}/${screenshot.screenshotPath}`}
                                    alt={`Screenshot ${screenshot.id}`}
                                    className="screenshot-image"
                                  />
                                </a>
                              </div>
                            )}
                          </div>
                        ))}
                      </Slider>
                    </div>
                  </div>
                )}
              </div>

              <aside className="product-aside" ref={purchaseAsideRef}>
                <div className={`purchase-card ${isPurchasePinned ? 'pinned' : ''}`} style={purchaseCardStyle}>
                  <h3>Get this product</h3>
                  <div className="purchase-price">
                    {isFreeProduct ? (
                      <span className="price-main">Free</span>
                    ) : discountedPrice !== null ? (
                      <>
                        <span className="price-original">${product.price}</span>
                        <span className="price-main">${discountedPrice}</span>
                      </>
                    ) : (
                      <span className="price-main">${product.price}</span>
                    )}
                  </div>
                  {isFreeProduct ? (
                    <div className="free-download-section">
                      <button className="download-button" onClick={handleFreeDownload} type="button">
                        Download Now
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="promo-container">
                        <input
                          id="promo-code-input"
                          name="promoCode"
                          type="text"
                          className="promo-input"
                          placeholder="Enter promo code"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        />
                        <button className="apply-button" onClick={handleApplyPromo} type="button">Apply</button>
                      </div>
                      {promoError && <p className="promo-error">{promoError}</p>}
                      {promoSuccess && <p className="promo-success">{promoSuccess}</p>}
                      {!proceedToPayPal && (
                        <button className="buy-now-button" onClick={handleBuyNowClick} type="button">Buy Now</button>
                      )}
                      {isPending ? (
                        <div className="loading-container"><div className="loading-spinner"></div></div>
                      ) : (
                        proceedToPayPal && (
                          <PayPalButtons
                            createOrder={createOrder}
                            onApprove={onApprove}
                            style={{ layout: 'vertical', color: 'black', shape: 'pill', size: 'large', label: 'pay' }}
                          />
                        )
                      )}
                    </>
                  )}
                  <div className="purchase-footnote">
                    <p className="muted-text">
                      By purchasing, you agree to our{' '}
                      <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms</Link> and{' '}
                      <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
                    </p>
                  </div>
                </div>
              </aside>
            </section>

            <section className="product-secondary">
              <div className="section-card" id="release-notes">
                <div className="release-notes-header">
                  <h2>Release notes</h2>
                  {product.currentVersion && (
                    <span className="release-version">Current version: {product.currentVersion}</span>
                  )}
                </div>
                <div className="release-note">
                  {product.releaseNotes ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{product.releaseNotes}</ReactMarkdown>
                  ) : (
                    <p className="muted-text">
                      Version history will appear here once releases are published.
                    </p>
                  )}
                </div>
              </div>

              <div className="section-card">
                <h2>Reviews</h2>
                <p className="muted-text">
                  Only verified purchasers can leave reviews. Reviews are published after approval.
                </p>
                <div className="review-list">
                  {reviews.length === 0 ? (
                    <div className="review-empty">
                      <p>No reviews yet.</p>
                    </div>
                  ) : (
                    reviews.map((review) => (
                      <div key={review.id} className="review-card">
                        <div className="review-header">
                          <div>
                            <p className="review-name">{review.userName || 'Verified User'}</p>
                            <p className="review-date">{new Date(review.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div className="review-rating">{String.fromCharCode(9733).repeat(review.rating)}</div>
                        </div>
                        {review.title && <h4>{review.title}</h4>}
                        {review.body && <p className="muted-text">{review.body}</p>}
                      </div>
                    ))
                  )}
                </div>
                {isLoggedIn ? (
                  <form className="review-form" onSubmit={handleSubmitReview}>
                    <h3>Leave a review</h3>
                    <label>
                      Rating
                      <select value={reviewRating} onChange={(e) => setReviewRating(parseInt(e.target.value, 10))}>
                        <option value={5}>5 - Excellent</option>
                        <option value={4}>4 - Good</option>
                        <option value={3}>3 - Average</option>
                        <option value={2}>2 - Below Average</option>
                        <option value={1}>1 - Poor</option>
                      </select>
                    </label>
                    <label>
                      Title
                      <input
                        type="text"
                        value={reviewTitle}
                        onChange={(e) => setReviewTitle(e.target.value)}
                        placeholder="Short summary"
                      />
                    </label>
                    <label>
                      Review
                      <textarea
                        value={reviewBody}
                        onChange={(e) => setReviewBody(e.target.value)}
                        rows="4"
                        placeholder="Share your experience"
                      />
                    </label>
                    {reviewError && <p className="error-text">{reviewError}</p>}
                    {reviewStatus && <p className="success-text">{reviewStatus}</p>}
                    <button type="submit" disabled={reviewSubmitting}>
                      {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                    </button>
                  </form>
                ) : (
                  <div className="review-empty">
                    <Link to="/login" className="ghost-cta">Sign in to review</Link>
                  </div>
                )}
              </div>
            </section>

            {/* Below the product, not beside the buy button — an affiliate offer competing with
                your own checkout would cost more in lost sales than it earns in commission. */}
            <DerivAffiliateBanner variant="product" compact />

            {showDialog && (
              <div className="confirmation-dialog">
                <div className="dialog-content">
                  <h3>Accept Terms and Privacy Policy</h3>
                  <p>
                    Please confirm that you agree to our{' '}
                    <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                      Terms and Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </Link>{' '}
                    before proceeding with your purchase.
                  </p>
                  <label>
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                    />
                    {/* The links belong in the sentence being agreed to, not only in the blurb
                        above it: this is the line that records consent, so what it refers to has
                        to be reachable from the line itself. Each opens in a new tab so a reader
                        never loses a checkout in progress. */}
                    <span>
                      I agree to the{' '}
                      <Link
                        to="/terms-and-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                      >
                        Terms and Conditions
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                      >
                        Privacy Policy
                      </Link>
                    </span>
                  </label>
                  <div className="dialog-actions">
                    <button
                      className="dialog-button"
                      onClick={handleConfirm}
                      disabled={!acceptedTerms}
                    >
                      Confirm
                    </button>
                    <button
                      className="dialog-button cancel"
                      onClick={() => {
                        setShowDialog(false);
                        setAcceptedTerms(false);
                        setProceedToPayPal(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </HelmetProvider>
  );
}

export default ProductPage;












